// iter-300 — Image Editor (modal + organized layout).
//
// Supersedes iter-296. Design goals this time:
//   • NOT a full-screen flat strip anymore — content is centred in a
//     max-w-6xl modal with proper margins so the editor doesn't feel
//     like a raw page.
//   • Three-section vertical layout:
//       1. Compact header: close + title + "Use this image" (sticky top)
//       2. Preview area in a card (dark glass), with a dedicated
//          side-by-side mode when the AI result is ready.
//       3. Toolbar card at the bottom with:
//            left column  → basic transforms (rotate/zoom/reset)
//            right column → 2-row grid of AI presets (hover-lift tiles)
//
// AI call still hits /api/ai/enhance-product-image on our backend;
// backend (iter-300) now proxies to admin bot ASB-AI-GEM-CGB-004.

import { useState, useEffect } from 'react';
import {
  X as XIcon, Check, RotateCw, RotateCcw, ZoomIn, ZoomOut,
  Sparkles, Loader2, RefreshCw, Image as ImageIcon, Wand2,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ─────────────── AI preset menu (user-asked) ─────────────── */
const AI_PRESETS = [
  { id: 'white_bg',      ar: 'خلفية بيضاء احترافية',  en: 'White BG',          emoji: '⬜' },
  { id: 'on_model',      ar: 'عرض على موديل',          en: 'On a model',        emoji: '👤' },
  { id: 'remove_bg',     ar: 'إزالة الخلفية',          en: 'Transparent BG',    emoji: '✂️' },
  { id: 'improve_light', ar: 'تحسين الإضاءة',           en: 'Better lighting',   emoji: '💡' },
  { id: 'lifestyle',     ar: 'مشهد حياتي',             en: 'Lifestyle scene',   emoji: '🏠' },
  { id: 'luxury',        ar: 'خلفية فاخرة',            en: 'Luxury gradient',   emoji: '✨' },
];

/* ─────────────── Helpers ─────────────── */

async function imageSourceToFile(src, fallbackName = 'photo.png') {
  if (!src) throw new Error('no source');
  if (src.startsWith('data:')) {
    const res = await fetch(src);
    const blob = await res.blob();
    return new File([blob], fallbackName, { type: blob.type || 'image/png' });
  }
  const res = await fetch(src, { mode: 'cors' });
  const blob = await res.blob();
  return new File([blob], fallbackName, { type: blob.type || 'image/png' });
}

async function renderToDataUrl(src, { rotation = 0, zoom = 1 } = {}) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = src;
  });
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const rotated90 = (((rotation % 360) + 360) % 360) % 180 !== 0;
  const outW = Math.round((rotated90 ? h : w) * zoom);
  const outH = Math.round((rotated90 ? w : h) * zoom);
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  ctx.save();
  ctx.translate(outW / 2, outH / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.scale(zoom, zoom);
  ctx.drawImage(img, -w / 2, -h / 2, w, h);
  ctx.restore();
  return canvas.toDataURL('image/png');
}

/* ─────────────── Main component ─────────────── */

export default function ImageEditor({
  sourceImage,         // { file?: File, preview: string }
  language = 'ar',
  isDarkMode = true,
  onCancel,
  onConfirm,
}) {
  const isRTL = language === 'ar';

  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);

  const [aiBusy, setAiBusy] = useState(false);
  const [aiPreset, setAiPreset] = useState(null);
  const [aiResultDataUrl, setAiResultDataUrl] = useState(null);
  const [showingAi, setShowingAi] = useState(false);

  const [livePreview, setLivePreview] = useState(sourceImage.preview);

  // Lock body scroll while modal is open so the page behind doesn't scroll.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Live preview — regenerate whenever rotation/zoom changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (rotation === 0 && zoom === 1) {
          setLivePreview(sourceImage.preview);
          return;
        }
        const url = await renderToDataUrl(sourceImage.preview, { rotation, zoom });
        if (!cancelled) setLivePreview(url);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [rotation, zoom, sourceImage.preview]);

  const applyPreset = async (preset) => {
    setAiBusy(true);
    setAiPreset(preset.id);
    setAiResultDataUrl(null);
    try {
      const token = localStorage.getItem('business_token');
      const srcForAI = livePreview || sourceImage.preview;
      const file = await imageSourceToFile(srcForAI, `product-${Date.now()}.png`);
      const fd = new FormData();
      fd.append('image', file);
      fd.append('preset', preset.id);
      const { data } = await axios.post(
        `${BACKEND_URL}/api/ai/enhance-product-image`,
        fd,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
      );
      const mime = data?.mime_type || 'image/png';
      const b64 = data?.enhanced_image_base64;
      if (!b64) throw new Error('empty');
      setAiResultDataUrl(`data:${mime};base64,${b64}`);
      setShowingAi(true);
      toast.success(language === 'ar' ? '✨ تم تحسين الصورة' : '✨ Image enhanced');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'failed';
      toast.error((language === 'ar' ? 'فشل التحسين: ' : 'Enhance failed: ') + String(detail).slice(0, 120));
    } finally {
      setAiBusy(false);
    }
  };

  const confirm = async () => {
    try {
      const finalDataUrl = showingAi && aiResultDataUrl ? aiResultDataUrl : livePreview;
      const file = await imageSourceToFile(finalDataUrl, `product-${Date.now()}.png`);
      onConfirm({ file, preview: finalDataUrl });
    } catch {
      toast.error(language === 'ar' ? 'تعذّر حفظ الصورة' : 'Could not save image');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm overflow-y-auto p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="image-editor-modal"
      dir={isRTL ? 'rtl' : 'ltr'}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        className="relative mx-auto my-4 w-full max-w-6xl rounded-2xl bg-[#0B1120] border border-white/10 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-white/10 bg-gradient-to-r from-[#0B1120] to-[#111c33]">
          <button
            onClick={onCancel}
            className="h-9 w-9 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition"
            data-testid="editor-close"
            aria-label="close"
          >
            <XIcon className="h-5 w-5" />
          </button>
          <h3 className="text-white font-bold flex items-center gap-2 text-sm sm:text-base">
            <Wand2 className="h-4 w-4 text-blue-400" />
            {language === 'ar' ? 'محرّر الصورة' : 'Image editor'}
          </h3>
          <button
            onClick={confirm}
            className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold text-xs sm:text-sm hover:opacity-90 shadow-lg shadow-emerald-500/20"
            data-testid="editor-confirm"
          >
            <Check className="h-4 w-4" />
            <span>{language === 'ar' ? 'استخدم هذه الصورة' : 'Use this image'}</span>
          </button>
        </div>

        {/* Preview card */}
        <div className="p-4 sm:p-6">
          <div className="rounded-xl bg-[#070B16] border border-white/5 p-3 sm:p-4">
            <div className={`grid gap-3 sm:gap-4 ${aiResultDataUrl ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
              <PreviewPanel
                title={language === 'ar' ? 'الأصلية / المعدّلة' : 'Original / edited'}
                src={livePreview}
                selected={!showingAi}
                onSelect={() => setShowingAi(false)}
                language={language}
                testId="panel-original"
              />
              {aiResultDataUrl && (
                <PreviewPanel
                  title={language === 'ar' ? 'نسخة الذكاء الاصطناعي' : 'AI version'}
                  src={aiResultDataUrl}
                  selected={showingAi}
                  onSelect={() => setShowingAi(true)}
                  language={language}
                  isAI
                  testId="panel-ai"
                />
              )}
            </div>
          </div>

          {/* Toolbar card — grid layout: transforms on one side, AI presets on the other */}
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4">
            {/* ─── Transforms ─── */}
            <div className="rounded-xl bg-[#0F1A2E] border border-white/5 p-3">
              <p className="text-[11px] font-bold text-white/60 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                <ImageIcon className="h-3 w-3" />
                {language === 'ar' ? 'التحويلات' : 'Transforms'}
              </p>
              <div className="grid grid-cols-3 gap-1.5">
                <ControlBtn testId="btn-rotate-left"  icon={RotateCcw} label={language === 'ar' ? 'يسار' : 'Left'}  onClick={() => setRotation(r => r - 90)} />
                <ControlBtn testId="btn-rotate-right" icon={RotateCw}  label={language === 'ar' ? 'يمين' : 'Right'} onClick={() => setRotation(r => r + 90)} />
                <ControlBtn testId="btn-reset"        icon={RefreshCw} label={language === 'ar' ? 'إرجاع' : 'Reset'} onClick={() => { setRotation(0); setZoom(1); }} />
                <ControlBtn testId="btn-zoom-out"     icon={ZoomOut}   label={language === 'ar' ? 'تصغير' : 'Out'}  onClick={() => setZoom(z => Math.max(0.3, +(z - 0.1).toFixed(2)))} />
                <ControlBtn testId="btn-zoom-in"      icon={ZoomIn}    label={language === 'ar' ? 'تكبير' : 'In'}   onClick={() => setZoom(z => Math.min(3, +(z + 0.1).toFixed(2)))} />
                <div className="col-span-1 flex items-center justify-center rounded-lg bg-white/5 text-white/70 text-[11px] font-bold" data-testid="zoom-badge">
                  {Math.round(zoom * 100)}%
                </div>
              </div>
            </div>

            {/* ─── AI presets ─── */}
            <div className="rounded-xl bg-[#0F1A2E] border border-white/5 p-3">
              <div className="flex items-center justify-between mb-2.5">
                <p className="text-[11px] font-bold text-white/60 uppercase tracking-wider flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3 text-amber-400" />
                  {language === 'ar' ? 'تحسين بالذكاء الاصطناعي' : 'AI enhance'}
                </p>
                {aiBusy && (
                  <span className="flex items-center gap-1 text-amber-400 text-[11px] font-semibold">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {language === 'ar' ? 'جارٍ المعالجة…' : 'Processing…'}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {AI_PRESETS.map((p) => {
                  const active = aiPreset === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => applyPreset(p)}
                      disabled={aiBusy}
                      className={`group relative flex flex-col items-start gap-1 px-2.5 py-2 rounded-lg border transition ${
                        active
                          ? 'bg-gradient-to-br from-amber-500/15 to-blue-500/15 border-amber-400/50 shadow-sm shadow-amber-500/10'
                          : 'bg-white/[0.03] border-white/5 hover:bg-white/[0.07] hover:border-white/15'
                      } ${aiBusy ? 'opacity-60 cursor-wait' : 'hover:-translate-y-0.5'}`}
                      data-testid={`ai-preset-${p.id}`}
                    >
                      <div className="text-lg leading-none">{p.emoji}</div>
                      <div className="text-[11px] text-white font-semibold leading-tight line-clamp-2">
                        {language === 'ar' ? p.ar : p.en}
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-[10px] text-white/40 leading-snug">
                {language === 'ar'
                  ? 'النسخة الأصلية تبقى معروضة بجانب نسخة الذكاء الاصطناعي — اضغط على أيّهما لتختارها.'
                  : 'Original stays visible beside the AI version — click either to pick it.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────── Sub-components ─────────────── */

function ControlBtn({ onClick, icon: Icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center justify-center gap-0.5 py-1.5 px-1 rounded-lg bg-white/5 hover:bg-white/15 text-white/90 transition group"
      data-testid={testId}
    >
      <Icon className="h-4 w-4 group-hover:scale-110 transition-transform" />
      <span className="text-[10px] font-semibold">{label}</span>
    </button>
  );
}

function PreviewPanel({ title, src, selected, onSelect, language, isAI, testId }) {
  return (
    <button
      onClick={onSelect}
      className={`group relative rounded-xl border-2 overflow-hidden transition-all text-start ${
        selected
          ? 'border-emerald-400 ring-2 ring-emerald-400/30 shadow-lg shadow-emerald-500/10'
          : 'border-white/10 hover:border-white/30 opacity-85 hover:opacity-100'
      }`}
      data-testid={testId}
    >
      <div className="bg-[#010409] flex items-center justify-center min-h-[260px] max-h-[55vh] overflow-hidden">
        <img src={src} alt={title} className="max-h-[55vh] w-auto object-contain" />
      </div>
      <div className="px-3 py-2 text-xs text-white font-semibold flex items-center justify-between gap-2 bg-black/60 backdrop-blur">
        <span className="flex items-center gap-1.5">
          {isAI && <Sparkles className="h-3.5 w-3.5 text-amber-400" />}
          {title}
        </span>
        {selected && (
          <span className="text-emerald-400 flex items-center gap-1">
            <Check className="h-3.5 w-3.5" />
            {language === 'ar' ? 'المختارة' : 'Picked'}
          </span>
        )}
      </div>
    </button>
  );
}
